// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { ACTIVE_ORGANIZATION_HEADER_NAME } from "containers/Auth/constants";

import { METHOD_NAMES } from "./constants";

type RequestMethodName = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

type RequestOptions = Omit<RequestInit, "method" | "headers"> & {
  headers?: HeadersInit;
};

type CreateRequestParams = {
  activeOrganizationId: string | number;
  url: string;
  abortSignal?: AbortSignal;
  options?: RequestOptions;
};

type RequestObject = {
  name: RequestMethodName;
  func: (params: CreateRequestParams) => Promise<Response>;
};

export const createRequest = (
  methodName: RequestMethodName,
  { activeOrganizationId, url, abortSignal, options = {} }: CreateRequestParams
): Promise<Response> => {
  const { headers } = options;

  const restOptions = { ...options };
  delete restOptions.headers;

  const contentType: Record<string, string> = {};
  if (options.body) {
    contentType["Content-Type"] = "application/vnd.api+json";
  }

  return fetch(url, {
    method: methodName,
    headers: {
      Accept: "application/vnd.api+json",
      [ACTIVE_ORGANIZATION_HEADER_NAME]: String(activeOrganizationId),
      ...contentType,
      ...headers,
    },
    credentials: "include",
    signal: abortSignal,
    ...restOptions,
  });
};

const createReqObj = (methodName: RequestMethodName): RequestObject => ({
  name: methodName,
  func: (params: CreateRequestParams) => createRequest(methodName, params),
});

export const get = createReqObj(METHOD_NAMES.GET as RequestMethodName);
export const post = createReqObj(METHOD_NAMES.POST as RequestMethodName);
export const patch = createReqObj(METHOD_NAMES.PATCH as RequestMethodName);
export const put = createReqObj(METHOD_NAMES.PUT as RequestMethodName);
export const del = createReqObj(METHOD_NAMES.DELETE as RequestMethodName);
